import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useServerPdfDownload } from '@/shared/hooks';
import { sanitizeDownloadFileName } from '@/shared/utils/downloadFile';
import { downloadOpportunityPdf } from '../api/opportunity.api';

export interface UseOpportunityPdfDownloadResult {
  /** The server renders per workspace, so there is nothing to download without one. */
  canDownload: boolean;
  download: () => Promise<void>;
  isDownloading: boolean;
}

/** Downloads an opportunity as the server-rendered PDF. */
export function useOpportunityPdfDownload(
  workspaceId: string | null | undefined,
  opportunity: { opportunityId: string; opportunityTitle?: string } | null | undefined,
): UseOpportunityPdfDownloadResult {
  const { t } = useTranslation('analysis');
  const pdf = useServerPdfDownload({
    preparing: t('opportunity.opportunityOverlayToolbar.exportingYourPdfThisMight'),
    failed: t('opportunity.opportunityOverlayToolbar.failedToDownloadPdfPlease'),
  });

  const opportunityId = opportunity?.opportunityId;
  const opportunityTitle = opportunity?.opportunityTitle;
  const { download: downloadPdf } = pdf;

  const download = useCallback(async () => {
    if (!workspaceId || !opportunityId) return;
    await downloadPdf({
      key: opportunityId,
      fetchBlob: () => downloadOpportunityPdf(workspaceId, opportunityId),
      // eslint-disable-next-line i18next/no-literal-string -- download filename slug, sanitized for the file system
      fileName: `${sanitizeDownloadFileName(opportunityTitle ?? '', 'opportunity')}.pdf`,
      analytics: { document_type: 'opportunity' },
    });
  }, [workspaceId, opportunityId, opportunityTitle, downloadPdf]);

  return {
    canDownload: !!workspaceId && !!opportunityId,
    download,
    isDownloading: !!opportunityId && pdf.isDownloading(opportunityId),
  };
}
